import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import LoginPage from './LoginPage';
import NavBar from './NavBar';
import UsersPage from './UsersPage';
import URLManagerPage from './URLManagerPage';
import DataImportPage from './DataImportPage';
import Dashboard from './components/Dashboard';
import PDFViewer from './components/PDFViewer';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const App = () => {
  const initialLoadRef = React.useRef(false);
  const [gifts, setGifts] = useState([]);
  const [currentUrl, setCurrentUrl] = useState('');
  const [currentPdf, setCurrentPdf] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState('all');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);


  const onLoginSuccess = (user, token) => {
    setIsAuthenticated(true);
    setUser(user);
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(user));
  };

  const fetchRandomGift = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/gifts/random`, {
        params: { source: selectedSource }
      });
      const { primaryGift, relatedGifts } = response.data;
      setGifts(relatedGifts);

      setCurrentUrl(primaryGift.source_url);
      // Construct PDF path
      const pdfPath = `${API_URL}/${primaryGift.source_pdf}`;

      setCurrentPdf(pdfPath);
    } catch (error) {
      console.error('Error fetching gifts:', error);
    }
    setLoading(false);
  }, [selectedSource,]);
  

  // Check for existing auth token on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // Configure axios to use the token
          const authConfig = {
            headers: { Authorization: `Bearer ${token}` }
          };
          
          // Verify token is valid with the server
          await axios.get(`${API_URL}/api/auth/status`, authConfig);
          
          // If we get here, token is valid
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth validation error:', error);
          // Token is invalid, clear storage
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
      setAuthLoading(false);
    };
    
    checkAuthStatus();
  }, []);

  useEffect(() => {
    // Only fetch gifts if authenticated
    if (isAuthenticated && !initialLoadRef.current) {
      initialLoadRef.current = true;
      fetchRandomGift();
    }
  }, [isAuthenticated, fetchRandomGift]);

  const handleStatusUpdate = async (id, status, denialReason = null) => {
    try {
      const token = localStorage.getItem('authToken');
      // Include the denial_reason in the request if it's provided and status is 'denied'
      const requestData = { status };
      if (status === 'denied' && denialReason) {
        requestData.denial_reason = denialReason;
      }

      await axios.put(`${API_URL}/api/gifts/${id}/status`, 
        requestData,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      // Update the status and denial_reason in the local state
      setGifts(gifts.map(gift => 
        gift.id === id ? { 
          ...gift, 
          status: status,
          denial_reason: status === 'denied' ? denialReason : gift.denial_reason
        } : gift
      ));
      
      // Check if all gifts are processed
      const remainingPending = gifts.filter(gift => 
        gift.id !== id && (gift.status === 'pending' || !gift.status)
      );
      
      if (remainingPending.length === 0) {
        // If all gifts are processed, fetch new random gift
        await fetchRandomGift();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  

  if (authLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={(user) => {
      setIsAuthenticated(true);
      setUser(user);
    }} />;
  }

  return (
    <Router>
    <div className="App">
      <NavBar 
  user={user} 
  onLogout={() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/login';
  }} 
/>
<Routes>
          {/* Add your routes here */}
          <Route path="/users" element={<UsersPage />} />
          <Route path="/url-manager" element={<URLManagerPage />} />
          <Route path="/data-import" element={<DataImportPage />} />
          <Route path="/login" element={<LoginPage onLoginSuccess={onLoginSuccess} />} />
          <Route path="/pdf-viewer" element={
            <PDFViewer 
              currentPdf={currentPdf}
              currentUrl={currentUrl}
              gifts={gifts}
              setGifts={setGifts}
              loading={loading}
              selectedSource={selectedSource}
              setSelectedSource={setSelectedSource}
              fetchRandomGift={fetchRandomGift}
              handleStatusUpdate={handleStatusUpdate}
            />
          } />
          <Route path="/" element={<Dashboard />} />
          </Routes>
    </div>
    </Router>
  );
};

export default App;

